/**
 * Custom hook for view management operations
 * Extracted from the main component to improve modularity and testability
 */
import { useCallback } from 'react';
import { successToast, errorToast } from 'utils/toast';
import { getLiteral } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';

/**
 * Custom hook for managing view operations (save, delete, change)
 * @param {Object} config - Configuration object
 * @param {Function} config.createView - Function to create a new view
 * @param {Function} config.removeView - Function to remove a view
 * @param {Function} config.clearFilters - Function to clear filters
 * @param {Function} config.customClearFilters - Custom function to clear filters
 * @param {Function} config.onChangeFilter - Function to change filter values
 * @param {Function} config.onDeleteFilter - Function to delete filter values
 * @param {Object} config.entity - Entity configuration
 * @param {boolean} config.avoidStorage - Whether to avoid storage operations
 * @param {Object} config.filters - Current filter values
 * @returns {Object} View management handlers
 */
const useViewManagement = ({
    createView,
    removeView,
    clearFilters,
    customClearFilters,
    onChangeFilter,
    onDeleteFilter,
    entity,
    avoidStorage,
    filters,
}) => {
    const onChangeView = useCallback(
        (view) => {
            onChangeFilter({
                filter: {
                    id: 'idView',
                    dataType: 'view',
                    hideForCount: false,
                },
                value: view,
            });
            logEvent({ event: entity.trueName, functionality: 'view' });
        },
        [onChangeFilter, entity],
    );

    const onChangeCustomView = useCallback(
        (view) => {
            if (!avoidStorage) {
                sessionStorage.removeItem(filters.customView.value.sessionStorageLabel);
            }

            onDeleteFilter(filters.customView.id);
            onChangeFilter({
                filter: {
                    id: 'idView',
                    dataType: 'view',
                    hideForCount: false,
                },
                value: view,
            });
        },
        [filters, onChangeFilter, onDeleteFilter, avoidStorage],
    );

    const onDeleteCustomView = useCallback(() => {
        if (!avoidStorage) {
            sessionStorage.removeItem(filters.customView.value.sessionStorageLabel);
        }

        onDeleteFilter(filters.customView.id);
        return Promise.resolve();
    }, [filters, onDeleteFilter, avoidStorage]);

    const onSaveView = useCallback(
        ({ name, isPrivate }) => {
            return new Promise((resolve, reject) => {
                const isPublic = !isPrivate;
                createView(entity, {
                    name,
                    isPublic,
                })
                    .then((view) => {
                        if (avoidStorage) {
                            customClearFilters && customClearFilters();
                        } else {
                            clearFilters({
                                entity,
                                isAPurge: false,
                                refresh: false,
                                clearAdvancedAndCrossFilters: true,
                            });
                        }

                        onChangeView({
                            value: view.id,
                            label: view.name,
                            item: view,
                        });
                        successToast({ text: getLiteral('label_view_successfully_created') });
                        resolve();
                    })
                    .catch((err) => {
                        console.error(err);
                        errorToast({ text: getLiteral('error_generalerror') });
                        reject();
                    });
            });
        },
        [createView, entity, clearFilters, onChangeView, avoidStorage, customClearFilters],
    );

    const onDeleteView = useCallback(
        (view) => {
            return new Promise((resolve, reject) => {
                removeView(view.item)
                    .then(() => {
                        successToast({
                            text: getLiteral('label_view_successfully_deleted'),
                        });
                        resolve();
                    })
                    .catch((err) => {
                        console.error(err);
                        errorToast({ text: getLiteral('error_generalerror') });
                        reject();
                    });

                onChangeView(null);
            });
        },
        [removeView, onChangeView],
    );

    const formatViews = useCallback((views, entity) => {
        const viewsName = entity.views;
        if (!views?.length > 0) return [];
        return views.reduce((obj, view) => {
            if (view.entity === viewsName) {
                obj.push({
                    value: parseInt(view.id, 10),
                    label: view.name,
                    item: view,
                });
            }
            return obj;
        }, []);
    }, []);

    const formatCustomView = useCallback((customView) => {
        if (!customView) return null;
        return {
            value: {
                value: customView.SelectFilterId,
                label: customView.label,
                item: {
                    id: customView.SelectFilterId,
                    isPublic: false,
                },
            },
            label: customView.label,
            id: 'customView',
            hideForCount: false,
        };
    }, []);

    return {
        onChangeView,
        onChangeCustomView,
        onDeleteCustomView,
        onSaveView,
        onDeleteView,
        formatViews,
        formatCustomView,
    };
};

export default useViewManagement;
